import { Component, ElementRef, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CommentsService } from '../../services/comments.service';
import { Comment, CommentRequest } from '../../models/comment.models';
import { AuthGuard } from '../../../../account/auth/app.gaurd';
import { UsersGridService } from '../../../../pages/users-grid/users-grid.service';
import { helperService } from '../../../../utils/helper';
import { UserProfileService } from '../../../../core/services/user.service';

@Component({
    selector: 'app-comments',
    templateUrl: './comments.component.html',
    styleUrls: ['./comments.component.scss'],
    standalone: false
})
export class CommentsComponent implements OnInit, OnDestroy {
  @Input() targetType: string = 'Task'; // 'Task' or 'Project'
  @Input() targetId: number = 0;
  @Input() visible: boolean = false;
  @Input() taskInfo: any = null; // Task information for display

  comments: Comment[] = [];
  commentForm!: UntypedFormGroup;
  loading: boolean = false;
  submitted: boolean = false;
  currentUserId: number = 0;
  userRoles: string[] = [];
  private readonly userDisplayNames = new Map<string, string>();
  private readonly expandedThreadIds = new Set<number>();
  shouldAutoScroll: boolean = true; // Control auto-scroll behavior
  savedScrollPosition: number = 0; // Save scroll position for context preservation

  constructor(
    private fb: UntypedFormBuilder,
    private commentsService: CommentsService,
    private readonly messageService: MessageService,
    private auth: AuthGuard,
    private userService: UsersGridService,
    private helperService: helperService,
    private userProfileService: UserProfileService,
    private host: ElementRef<HTMLElement>
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.userRoles = this.auth.roles || [];
    this.loadUserDisplayNames();
    this.getLoggedInUser();
  }

  private loadUserDisplayNames(): void {
    this.userProfileService.getUserNamesAndEmails().subscribe({
      next: response => {
        const users = Array.isArray(response?.data) ? response.data : [];

        users.forEach((user: any) => {
          const email = String(user?.email || '').trim();
          const fullName = String(user?.fullName || '').trim();

          if (email) {
            this.userDisplayNames.set(email.toLowerCase(), fullName || email);
          }
        });
      },
      error: error => {
        console.error('Error loading comment author names:', error);
      }
    });
  }

  getCommentAuthor(createdBy: string | null | undefined): string {
    const email = String(createdBy || '').trim();
    return this.userDisplayNames.get(email.toLowerCase()) || email || 'Unknown user';
  }

  getAuthorInitials(createdBy: string | null | undefined): string {
    const name = this.getCommentAuthor(createdBy).split('@')[0];
    const parts = name.split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const initials = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0];
    return initials.toUpperCase();
  }

  getCommentAuthorTitle(createdBy: string | null | undefined): string {
    const email = String(createdBy || '').trim();
    return this.getCommentAuthor(email) !== email ? email : '';
  }

  private showSuccessMessage(detail: string): void {
    this.messageService.add({
      severity: 'success',
      detail,
      life: 3000
    });
  }

  private getLoggedInUser(): void {
    this.userService.getLoginUser().subscribe(
      response => {
        if (response && response.success && response.rows) {
          this.currentUserId = response.rows.id;
          this.loadComments();
        } else {
          console.error('Failed to get logged in user details');
        }
      },
      error => {
        console.error('Error getting logged in user:', error);
        this.loadComments(); // Load comments anyway, but permissions will be limited
      }
    );
  }

  ngOnDestroy(): void {
    // Clean up any subscriptions if needed
    window.removeEventListener('keydown', this.deleteEscapeGuard, true);
  }

  private initializeForm(): void {
    this.commentForm = this.fb.group({
      commentText: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1000)]]
    });
  }

  loadComments(): void {
    if (!this.targetId || !this.targetType) {
      return;
    }

    this.loading = true;
    this.commentsService.getCommentsByTarget(this.targetType, this.targetId).subscribe(
      response => {
        this.loading = false;
        
        // Backend response format: {data: [...], message: "Target comments retrieved successfully", customValues: null}
        if (response.data && Array.isArray(response.data)) {
          this.comments = this.commentsService.organizeCommentsIntoThreads(response.data)
            .sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
          this.setCommentPermissions();
          
          if (this.shouldAutoScroll) {
            setTimeout(() => this.scrollToLatestComment(), 0);
          } else {
            setTimeout(() => this.restoreScrollPosition(), 0);
          }

          // Reset the flag for next time
          this.shouldAutoScroll = true;
        } else {
          this.messageService.add({ 
            severity: 'error', 
            detail: response.message || 'Failed to load comments' 
          });
        }
      },
      error => {
        this.loading = false;
        console.error('Error loading comments:', error);
        this.messageService.add({ 
          severity: 'error', 
          detail: 'Failed to load comments' 
        });
      }
    );
  }

  addComment(): void {
    this.submitted = true;
    
    if (this.commentForm.invalid) {
      return;
    }

    const commentRequest: CommentRequest = {
      commentText: this.commentForm.value.commentText.trim(),
      targetType: this.targetType,
      targetId: this.targetId,
      parentCommentId: null
    };

    this.loading = true;
    this.commentsService.createComment(commentRequest).subscribe(
      response => {
        this.loading = false;
        this.submitted = false;
        
        // Backend response format: {data: {...}, message: "Comment created successfully", customValues: null}
        // Consider success if we have data or a success message
        const isSuccess = response.data || (response.message && response.message.includes('successfully'));
        
        if (isSuccess) {
          this.showSuccessMessage(response.message || 'Comment added successfully');
          this.commentForm.reset();
          this.shouldAutoScroll = true; // New main comments should scroll to bottom
          this.loadComments(); // Reload to get updated list (auto-scrolls to latest)
        } else {
          this.messageService.add({ 
            severity: 'error', 
            detail: response.message || 'Failed to add comment' 
          });
        }
      },
      error => {
        this.loading = false;
        this.submitted = false;
        console.error('Error adding comment:', error);
        this.messageService.add({ 
          severity: 'error', 
          detail: 'Failed to add comment' 
        });
      }
    );
  }

  addReply(parentComment: Comment): void {
    if (!parentComment.replyText || parentComment.replyText.trim() === '') {
      return;
    }

    this.expandedThreadIds.add(parentComment.id);

    const commentRequest: CommentRequest = {
      commentText: parentComment.replyText.trim(),
      targetType: this.targetType,
      targetId: this.targetId,
      parentCommentId: parentComment.id
    };

    this.commentsService.createComment(commentRequest).subscribe(
      response => {
        // Backend response format: {data: {...}, message: "Comment created successfully", customValues: null}
        const isSuccess = response.data || (response.message && response.message.includes('successfully'));
        
        if (isSuccess) {
          this.showSuccessMessage(response.message || 'Reply added successfully');
          parentComment.replyText = '';
          parentComment.showReplyForm = false;
          this.saveScrollPosition(); // Save current scroll position
          this.shouldAutoScroll = false; // Don't auto-scroll for replies - keep user in context
          this.loadComments(); // Reload to get updated list
        } else {
          this.messageService.add({ 
            severity: 'error', 
            detail: response.message || 'Failed to add reply' 
          });
        }
      },
      error => {
        console.error('Error adding reply:', error);
        this.messageService.add({ 
          severity: 'error', 
          detail: 'Failed to add reply' 
        });
      }
    );
  }

  editComment(comment: Comment): void {
    if (!comment.editText || comment.editText.trim() === '') {
      return;
    }

    this.commentsService.updateComment(comment.id, {
      commentId: comment.id,
      commentText: comment.editText.trim()
    }).subscribe(
      response => {
        // Backend response format: {data: {...}, message: "Comment updated successfully", customValues: null}
        const isSuccess = response.data || (response.message && response.message.includes('successfully'));
        
        if (isSuccess) {
          this.showSuccessMessage(response.message || 'Comment updated successfully');
          comment.isEditing = false;
          this.loadComments(); // Reload to get updated comment
        } else {
          this.messageService.add({ 
            severity: 'error', 
            detail: response.message || 'Failed to update comment' 
          });
        }
      },
      error => {
        console.error('Error updating comment:', error);
        this.messageService.add({ 
          severity: 'error', 
          detail: 'Failed to update comment' 
        });
      }
    );
  }

  pendingDeleteComment: Comment | null = null;

  private readonly deleteEscapeGuard = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.pendingDeleteComment) {
      event.stopPropagation();
      this.onDeleteConfirm('No');
    }
  };

  deleteComment(comment: Comment): void {
    this.pendingDeleteComment = comment;
    window.addEventListener('keydown', this.deleteEscapeGuard, true);
  }

  onDeleteConfirm(answer: string): void {
    window.removeEventListener('keydown', this.deleteEscapeGuard, true);
    const comment = this.pendingDeleteComment;
    this.pendingDeleteComment = null;
    if (answer === 'Yes' && comment) {
      this.commentsService.deleteComment(comment.id).subscribe(
        response => {
          // Backend response format: {data: {...}, message: "Comment deleted successfully", customValues: null}
          const isSuccess = response.data || (response.message && response.message.includes('successfully'));
          
          if (isSuccess) {
            this.showSuccessMessage(response.message || 'Comment deleted successfully');
            this.loadComments(); // Reload to remove deleted comment
          } else {
            this.messageService.add({ 
              severity: 'error', 
              detail: response.message || 'Failed to delete comment' 
            });
          }
        },
        error => {
          console.error('Error deleting comment:', error);
          this.messageService.add({ 
            severity: 'error', 
            detail: 'Failed to delete comment' 
          });
        }
      );
    }
  }

  toggleReplyForm(comment: Comment): void {
    comment.showReplyForm = !comment.showReplyForm;
    if (comment.showReplyForm) {
      comment.replyText = '';
    }
  }

  startEditing(comment: Comment): void {
    comment.isEditing = true;
    comment.editText = comment.commentText;
  }

  cancelEditing(comment: Comment): void {
    comment.isEditing = false;
    comment.editText = '';
  }

  cancelReply(comment: Comment): void {
    comment.showReplyForm = false;
    comment.replyText = '';
  }

  toggleCollapse(comment: Comment): void {
    if (comment.isCollapsed) {
      this.expandThread(comment);
    } else {
      comment.isCollapsed = true;
      this.expandedThreadIds.delete(comment.id);
    }
  }

  private expandThread(comment: Comment): void {
    comment.isCollapsed = false;
    this.expandedThreadIds.add(comment.id);
    comment.replies?.forEach(reply => this.expandThread(reply));
  }

  private get listElement(): HTMLElement | null {
    return this.host.nativeElement.querySelector('.comments-list');
  }

  scrollToLatestComment(): void {
    const list = this.listElement;
    if (list) list.scrollTop = list.scrollHeight;
  }

  saveScrollPosition(): void {
    this.savedScrollPosition = this.listElement?.scrollTop ?? 0;
  }

  restoreScrollPosition(): void {
    const list = this.listElement;
    if (list) list.scrollTop = this.savedScrollPosition;
  }

  private setCommentPermissions(): void {
    this.comments.forEach(comment => {
      this.setCommentPermissionsRecursive(comment);
    });
  }

  private setCommentPermissionsRecursive(comment: Comment): void {
    comment.canEdit = this.commentsService.canEditComment(comment, this.currentUserId, this.userRoles);
    comment.canDelete = this.commentsService.canDeleteComment(comment, this.currentUserId, this.userRoles);
    
    comment.isCollapsed = !this.expandedThreadIds.has(comment.id);
    
    const isAdmin = this.helperService.isAdminUser(this.userRoles);
    const isOwnComment = comment.fkUserId === this.currentUserId;

    if (comment.replies && comment.replies.length > 0) {
      comment.replies.forEach(reply => {
        this.setCommentPermissionsRecursive(reply);
      });
    }
  }

  get commentText() {
    return this.commentForm.get('commentText');
  }
}
